(() => {
  'use strict';
  const configNode = document.getElementById('codebrwn-gokwik-config');
  if (!configNode || window.codebrwnGoKwik) return;
  const config = JSON.parse(configNode.textContent);
  const root = String(config.root || '/').replace(/\/?$/, '/');
  const state = { country: null, active: false, busy: false };
  window.codebrwnGoKwik = state;
  let sdkPromise;
  let stopCartWatch = () => {};
  const nativeBuyNow = new WeakSet();
  const pendingBuyNow = new WeakMap();
  const countryCacheKey = 'codebrwn-checkout-country-v1';
  const countryCacheLifetime = 30 * 60 * 1000;

  function setCountry(country) {
    if (typeof country !== 'string' || !/^[A-Z]{2}$/.test(country)) return;
    state.country = country;
    state.active = country === 'IN';
    if (state.active) loadSdk().catch(() => {});
  }

  function restoreCountry() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(countryCacheKey));
      if (cached?.expiresAt > Date.now() && cached.expiresAt <= Date.now() + countryCacheLifetime) setCountry(cached.country);
    } catch (_) {
      // Storage restrictions must not prevent checkout.
    }
  }

  // A currency preference is not evidence that the visitor is in India.
  async function detectCountry() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000);
    try {
      const response = await fetch(`${root}browsing_context_suggestions.json`, {
        credentials: 'same-origin', signal: controller.signal,
      });
      if (!response.ok) return;
      const data = await response.json();
      const detected = data.detected_values;
      setCountry(detected?.country?.handle || (detected?.country_name === 'India' ? 'IN' : null));
      if (state.country) {
        try {
          sessionStorage.setItem(countryCacheKey, JSON.stringify({ country: state.country, expiresAt: Date.now() + countryCacheLifetime }));
        } catch (_) {
          // Detection remains usable when storage is unavailable.
        }
      }
    } catch (_) {
      // Country lookup failures leave native Shopify checkout untouched.
    } finally {
      clearTimeout(timer);
    }
  }

  function showCheckoutError(control, message) {
    let status = document.getElementById('codebrwn-checkout-error');
    if (!status) {
      status = document.createElement('p');
      status.id = 'codebrwn-checkout-error';
      status.setAttribute('role', 'alert');
      control.button.closest('.shopify-payment-button')?.after(status);
      if (!status.isConnected) control.button.after(status);
    }
    status.textContent = message;
  }

  function loadSdk() {
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise((resolve, reject) => {
      window.merchantInfo = { ...config.merchantInfo };
      window.gaTag = { ga4: config.ga4 || '' };
      const timer = setTimeout(() => finish(new Error('GoKwik SDK timed out')), 10000);
      let interval;
      function finish(error) {
        clearTimeout(timer);
        clearInterval(interval);
        window.removeEventListener('gokwikLoaded', check);
        if (error) reject(error);
        else resolve(window.gokwikSdk);
      }
      function check() {
        if (typeof window.gokwikSdk?.initCheckout === 'function' && typeof window.onBuyNowClick === 'function') finish();
      }
      window.addEventListener('gokwikLoaded', check);
      interval = setInterval(check, 100);
      const script = document.createElement('script');
      script.src = 'https://pdp.gokwik.co/merchant-integration/build/merchant.integration.js?v4';
      script.async = true;
      script.onerror = () => finish(new Error('GoKwik SDK failed to load'));
      document.head.append(script);
      check();
    }).catch(error => {
      sdkPromise = null;
      throw error;
    });
    return sdkPromise;
  }

  function isCheckoutLink(element) {
    if (!element.matches('a[href]')) return false;
    const url = new URL(element.href, window.location.href);
    return url.origin === window.location.origin && /\/checkout(?:\/|$)/.test(url.pathname);
  }

  function controlFromEvent(event) {
    const path = event.composedPath().filter(node => node instanceof Element);
    // Never claim Add to cart, including its icons and loading spinner.
    if (path.some(node => node.matches('[name="add"], .product-form__submit'))) return null;
    for (const node of path) {
      if (node.matches('button[data-codebrwn-buy-now]')) {
        const form = node.closest('form[action*="/cart/add"]');
        if (form) return { button: node, type: 'buyNow', form };
      }
      if (node.matches('.shopify-payment-button__more-options')) return null;
      if (node.matches('.shopify-payment-button__button, shopify-buy-it-now-button, shopify-accelerated-checkout')) {
        const form = node.closest('form[action*="/cart/add"]');
        if (form) return { button: node, type: 'buyNow', form };
      }
      if (node.matches('button[name="checkout"], input[name="checkout"], shopify-accelerated-checkout-cart') || isCheckoutLink(node)) {
        return { button: node, type: 'checkout' };
      }
      if (node.closest('#wp-cart-root') && (node.matches('.checkout-button') || (node.matches('a') && /^\/cart\/?$/.test(node.getAttribute('href') || '')))) {
        return { button: node, type: 'checkout' };
      }
    }
    return null;
  }

  function productItem(form) {
    if (!form.reportValidity()) return null;
    if (form.querySelector('[name="add"]')?.disabled) return null;
    const data = new FormData(form);
    const id = data.get('id');
    const quantity = Number(data.get('quantity') || 1);
    if (!id || !Number.isInteger(quantity) || quantity < 1) return null;
    const item = { id: String(id), quantity };
    if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      // Uploads need Shopify's native multipart product form.
      if (value instanceof File && value.size) return { unsupportedUpload: true };
      if (match && typeof value === 'string') (item.properties ||= {})[match[1]] = value;
    }
    return item;
  }

  function releaseCartFocus() {
    window.WPCart?.close?.();
    document.querySelector('cart-drawer.active')?.close?.();
  }

  function keepCartClosed(sdk) {
    stopCartWatch();
    const observer = new MutationObserver(releaseCartFocus);
    document.querySelectorAll('#wp-cart-root, cart-drawer').forEach(drawer => {
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });
    stopCartWatch = () => observer.disconnect();
    sdk.on?.('modal_closed', () => stopCartWatch());
    releaseCartFocus();
  }

  async function cartRequest(path, body) {
    const response = await fetch(`${root}${path}`, {
      credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000),
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) throw new Error('The cart could not be prepared.');
    return response.json();
  }

  async function prepareAutomaticCap(control, item) {
    const node = document.getElementById('wp-cart-bootstrap');
    if (!node || document.getElementById('wp-cart-private-preview-banner')) return false;
    const bootstrap = JSON.parse(node.textContent);
    if (bootstrap.shop !== 'codebrwn-2.myshopify.com' || !bootstrap.proxyUrl) return false;
    const response = await fetch(bootstrap.proxyUrl, {
      credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000),
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
    });
    if (!response.ok) throw new Error('The offer could not be checked.');
    const published = await response.json();
    if (published.degraded || !published.config) throw new Error('The offer could not be checked.');
    if (!published.config.enabled || !published.featureFlags?.advancedPromotions) return false;
    const productId = Number(control.button.getAttribute('data-product-id') || new FormData(control.form).get('product-id'));
    const now = Date.now();
    const offer = (published.config.offers || []).find(offer =>
      offer.enabled && offer.type === 'gift' && offer.autoAdd && offer.id.startsWith('live-cap:') &&
      (!offer.startsAt || now >= Date.parse(offer.startsAt)) &&
      (!offer.endsAt || now <= Date.parse(offer.endsAt)) &&
      (!offer.marketCodes?.length || offer.marketCodes.includes(bootstrap.market)) &&
      offer.productIds?.map(Number).includes(productId),
    );
    if (!offer) return false;
    if (item.unsupportedUpload) throw new Error('Please add this item to your cart first.');
    const deadline = Date.now() + 6000;
    while (!window.WPCart?.getConfig?.().offers?.some(current => current.id === offer.id && current.autoAdd)) {
      if (Date.now() >= deadline) throw new Error('The free cap is not ready.');
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    keepCartClosed(window.gokwikSdk || {});
    const signature = JSON.stringify(item);
    const previous = pendingBuyNow.get(control.form);
    let cart = await cartRequest('cart.js');
    const alreadyAdded = previous?.signature === signature && cart.items.some(line =>
      line.key === previous.key && line.quantity >= previous.quantity,
    );
    if (!alreadyAdded) {
      const added = await cartRequest('cart/add.js', { items: [item] });
      const line = added.items?.[0];
      if (line) pendingBuyNow.set(control.form, { signature, key: line.key, quantity: line.quantity });
    }
    // refresh() also loads upsells and reviews. Start gift synchronization once,
    // then verify the Shopify cart directly without waiting on that extra work.
    void Promise.resolve(window.WPCart.refresh()).catch(() => {});
    const giftDeadline = Date.now() + 10000;
    while (Date.now() < giftDeadline) {
      cart = await cartRequest('cart.js');
      const gifts = cart.items.filter(line => line.properties?._wp_cart_offer_id === offer.id);
      if (gifts.length === 1 && gifts[0].quantity === 1 &&
          offer.recommendedVariantIds.map(Number).includes(Number(gifts[0].variant_id)) &&
          Number(gifts[0].final_line_price) === 0) return cart;
      if (gifts.some(line => Number(line.final_line_price) > 0)) {
        await cartRequest('cart/update.js', { updates: Object.fromEntries(gifts.map(line => [line.key, 0])) });
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 150));
    }
    throw new Error('The free cap could not be confirmed.');
  }

  function showGiftProgress() {
    const progress = document.createElement('div');
    progress.id = 'codebrwn-free-cap-progress';
    progress.setAttribute('role', 'status');
    progress.setAttribute('aria-live', 'polite');
    progress.style.cssText = 'position:fixed;z-index:2147483646;top:50%;left:50%;transform:translate(-50%,-50%);width:max-content;max-width:calc(100% - 32px);box-sizing:border-box;padding:20px 26px;border-radius:12px;background:#111;color:#fff;box-shadow:0 8px 32px #0003;font:600 18px/1.4 system-ui,sans-serif;text-align:center;pointer-events:none;';
    progress.textContent = 'Adding your free cap…';
    document.body.append(progress);
    return progress;
  }

  async function celebrateFreeCap() {
    // Only called once the gift synchronizer has verified a zero-price cap.
    // Decorative effects must never prevent the checkout handoff.
    let celebration;
    try {
      document.getElementById('codebrwn-free-cap-celebration')?.remove();
      celebration = document.createElement('div');
      celebration.id = 'codebrwn-free-cap-celebration';
      celebration.style.cssText = 'position:fixed;inset:0;z-index:2147483647;pointer-events:none;overflow:hidden;';
      const message = document.createElement('div');
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      message.setAttribute('aria-atomic', 'true');
      message.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:max-content;max-width:calc(100% - 32px);box-sizing:border-box;padding:20px 26px;border-radius:12px;background:#111;color:#fff;box-shadow:0 8px 32px #0003;font:600 18px/1.4 system-ui,sans-serif;text-align:center;';
      celebration.append(message);
      document.body.append(celebration);
      // Insert text after the live region is mounted for screen readers.
      await new Promise(resolve => setTimeout(resolve, 30));
      message.textContent = 'Free Cap Added to your cart';
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (!reducedMotion) {
        const colors = ['#b98b55', '#eacb97', '#fff0c9', '#f0b85b', '#78624b'];
        for (let index = 0; index < 48; index += 1) {
          const piece = document.createElement('span');
          piece.setAttribute('aria-hidden', 'true');
          piece.style.cssText = `position:absolute;left:50%;top:50%;width:8px;height:12px;background:${colors[index % colors.length]};border-radius:2px;`;
          celebration.append(piece);
          const angle = Math.PI + (index / 47) * Math.PI;
          const distance = 130 + Math.random() * 260;
          const x = Math.cos(angle) * distance;
          const y = Math.sin(angle) * distance;
          piece.animate?.([
            { transform: 'translate(-50%,0) rotate(0deg)', opacity: 1 },
            { transform: `translate(${x}px,${y}px) rotate(${index * 27}deg)`, opacity: 1, offset: 0.45 },
            { transform: `translate(${x * 1.3}px,${y + 420}px) rotate(${index * 54}deg)`, opacity: 0 },
          ], { duration: 2200, easing: 'cubic-bezier(.15,.65,.4,1)', fill: 'forwards' });
        }
      }
      await new Promise(resolve => setTimeout(resolve, 2400));
    } catch (_) {
      // Continue checkout if the browser cannot render the celebration.
    } finally {
      celebration?.remove();
    }
  }

  async function openCheckout(control, item) {
    state.busy = true;
    control.button.setAttribute('aria-busy', 'true');
    const giftButton = control.button.hasAttribute('data-codebrwn-buy-now');
    const originalLabel = giftButton ? control.button.textContent : null;
    let progress;
    try {
      if (giftButton) {
        control.button.setAttribute('aria-disabled', 'true');
        control.button.textContent = 'Adding your free cap…';
        try { progress = showGiftProgress(); } catch (_) { /* Button still provides feedback. */ }
      }
      document.getElementById('codebrwn-checkout-error')?.remove();
      if (item?.unsupportedUpload) {
        showCheckoutError(control, 'Please add this item to your cart, then use GoKwik checkout.');
        return;
      }
      // Gift synchronization and provider startup can run independently.
      const [loadedSdk, automaticCart] = await Promise.all([
        state.active ? loadSdk().catch(error => ({ error })) : null,
        control.type === 'buyNow' ? prepareAutomaticCap(control, item) : false,
      ]);
      if (loadedSdk?.error) throw loadedSdk.error;
      const sdk = window.gokwikSdk || loadedSdk;
      if (control.button.hasAttribute('data-codebrwn-buy-now') && !automaticCart) {
        throw new Error('The automatic Free Cap offer could not be verified.');
      }
      progress?.remove();
      if (!state.active) {
        if (automaticCart) {
          void celebrateFreeCap();
          pendingBuyNow.delete(control.form);
          releaseCartFocus();
          window.location.assign(`${root}checkout`);
        } else {
          nativeBuyNow.add(control.button);
          try { control.button.click(); } finally { nativeBuyNow.delete(control.button); }
        }
        return;
      }
      // Release cart/quick-view focus traps before the checkout overlay opens.
      keepCartClosed(sdk);
      control.form?.closest('quick-add-modal')?.hide?.(true);
      if (control.type === 'buyNow' && !automaticCart) {
        // The provider's helper accepts a button for its loader plus explicit
        // line items. Keep its loader off the existing Shopify button markup.
        const loaderButton = document.createElement('button');
        loaderButton.append(document.createElement('span'));
        window.onBuyNowClick(loaderButton, false, [item]);
      } else {
        delete window.merchantInfo.buyNowProducts;
        const checkoutInfo = { ...window.merchantInfo };
        if (automaticCart) {
          delete checkoutInfo.buyNowCart;
          delete checkoutInfo.buyNowProducts;
          delete checkoutInfo.mmData;
          delete checkoutInfo.checkoutType;
          delete checkoutInfo.cart;
          delete checkoutInfo.appCart;
          delete checkoutInfo.appFlow;
        }
        sdk.initCheckout(checkoutInfo);
        // Render over the provider's loading screen while checkout starts.
        if (automaticCart) void celebrateFreeCap();
      }
      if (automaticCart) pendingBuyNow.delete(control.form);
    } catch (error) {
      stopCartWatch();
      console.warn('GoKwik unavailable; checkout can be retried.', error);
      showCheckoutError(control, pendingBuyNow.has(control.form)
        ? 'Your T-shirt is in the cart, but checkout could not confirm the free cap. Please retry or open your cart to check it.'
        : 'Checkout couldn’t load. Please check your connection and click checkout again to retry.');
    } finally {
      progress?.remove();
      control.button.removeAttribute('aria-busy');
      // Prevent double-clicks while the provider is opening its overlay.
      setTimeout(() => {
        if (giftButton) {
          control.button.removeAttribute('aria-disabled');
          control.button.textContent = originalLabel;
        }
        state.busy = false;
      }, 1000);
    }
  }

  function route(control, event) {
    if (state.busy || control.button.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (control.button.disabled || control.button.getAttribute('aria-disabled') === 'true') return;
    const item = control.type === 'buyNow' ? productItem(control.form) : null;
    if (control.type === 'buyNow' && !item) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!state.busy) void openCheckout(control, item);
  }

  // Capture delegated events so newly rendered cart drawers and quick views
  // work without cloning buttons or removing Dawn's existing listeners.
  window.addEventListener('click', event => {
    if (event.defaultPrevented || event.button > 0) return;
    const control = controlFromEvent(event);
    if (control && nativeBuyNow.has(control.button)) return;
    if (!state.active && (control?.type !== 'buyNow' || !document.getElementById('wp-cart-bootstrap'))) return;
    if (control) route(control, event);
  }, true);
  window.addEventListener('submit', event => {
    if (!state.active || event.defaultPrevented) return;
    const button = event.submitter;
    if (button?.matches('[name="checkout"]')) route({ button, type: 'checkout' }, event);
  }, true);
  function enableCapBuyNow() {
    document.querySelectorAll('button[data-codebrwn-buy-now]').forEach(button => {
      const add = button.closest('form')?.querySelector('[name="add"]');
      const disabled = !add || add.disabled;
      if (button.disabled !== disabled) button.disabled = disabled;
    });
  }
  const capButtonObserver = new MutationObserver(enableCapBuyNow);
  capButtonObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  enableCapBuyNow();
  // Detect once on entry, preload GoKwik for India, and reuse the result on
  // subsequent pages. Checkout never waits for a location request.
  restoreCountry();
  if (!state.country) void detectCountry();
})();
